import CalendarMonth from '@mui/icons-material/CalendarMonth';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { SingleInputDateRangePicker } from '~components/FormFields/SingleInputDateRangePicker';

import { CONTROLS_HEIGHT_IN_PX } from '../constants';

interface DateRangeSelectorProps {
  onChange: (startDate?: string, endDate?: string) => void;
}

export default function DateRangeSelector({ onChange }: DateRangeSelectorProps) {
  const theme = useTheme();
  const [showDateRateSelector, setShowDateRateSelector] = useState(false);

  const {
    control,
    formState: { errors },
    watch,
  } = useForm({
    mode: 'onBlur',
    defaultValues: { dateRange: [null, null] },
  });

  const [selectedStartDate, selectedEndDate] = watch('dateRange') as [
    Dayjs | null,
    Dayjs | null,
  ];

  useEffect(() => {
    if (!showDateRateSelector) {
      return;
    }

    const startDate =
      selectedStartDate && selectedStartDate.isValid()
        ? selectedStartDate.format('YYYY-MM-DD')
        : undefined;
    const endDate =
      selectedEndDate && selectedEndDate.isValid()
        ? selectedEndDate.format('YYYY-MM-DD')
        : undefined;

    onChange(startDate, endDate);
  }, [onChange, showDateRateSelector, selectedStartDate, selectedEndDate]);

  return (
    <Box
      sx={{
        flex: 0,
        minWidth: showDateRateSelector ? '200px' : undefined,
      }}
    >
      {showDateRateSelector ? (
        <SingleInputDateRangePicker
          InputProps={{
            sx: {
              py: 0,
              '& .MuiInputBase-input': { py: 0, height: CONTROLS_HEIGHT_IN_PX },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.brandV2.colors.treadGray7,
              },
            },
          }}
          control={control}
          defaultOpen
          errors={errors}
          name="dateRange"
          sx={{ width: '100%', mt: 0 }}
        />
      ) : (
        <Button
          onClick={() => setShowDateRateSelector(true)}
          startIcon={<CalendarMonth />}
          sx={{
            '&.MuiButton-root': {
              backgroundColor: theme.semantic.surfaceRaised,
              color: theme.palette.text.primary,
              border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
              borderRadius: theme.brandV2.borderRadius,
              boxShadow: 'none',
            },
          }}
          data-ui="page-dispatch__copy-vendor-assignments__date-filter-button"
        >
          {t('common.date')}
        </Button>
      )}
    </Box>
  );
}
